// İSG uzmanı portalı — Belge Şemaları: belge türü oluştur/düzenle formu (Modal + RHF + Zod).
// GL #7 contract §4.1.3 `POST /document-schemas/types`, §4.1.4 `PUT /document-schemas/types/{uuid}`.
// Düzen tasarım review §2.2: belge sahibi|kod ve kural|biçim iki sütun, etiketler tam genişlik
// ("Etiket" bölümü), kurallar "Kurallar" bölümünde. Seçiciler yerel `Select`, ipuçları satır içi
// (`SelectMenu`/`Tooltip` Modal örtüsünün altında açılır). Precedent: `PermitTypeFormModal`.
//
// Yalnız açıkken monte edilir (çağıran `key` verir) → RHF `defaultValues` her açılışta taze.
// Düzenlemede belge sahibi ve kod salt-okunur metindir (değişmez; gövdede yok). "Aktif" yalnız
// düzenlemededir (yeni tür aktif başlar).
//
// Onay (D1/D2, review §2.4): pasifleştirme ya da geçerlilik kuralı değişikliği TÜM firmalarda
// geriye dönük etkilidir. `ConfirmDialog` formun ÜSTÜNDE saf bir kapıdır: "onayla" onu kapatır ve
// formun KENDİ mutasyonuyla gönderir — her hata (alan, 403, 404) formda görünür.

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  ConfirmDialog,
  type CreateDocumentTypeRequest,
  DOCUMENT_ENTITIES,
  type DocumentCaptureFormat,
  type DocumentEntity,
  type DocumentExpiryRule,
  type DocumentTypeAdminDto,
  Field,
  FormSection,
  Input,
  Modal,
  Select,
  type SelectOption,
  Switch,
  type UpdateDocumentTypeRequest,
  documentSchemasApi,
  documentTypeLabel,
  fieldDescribedBy,
  hasErrorCode,
  isApiError,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { type RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { type ConfirmDiff, diffNeedsConfirm, needsConfirm } from '../../lib/schemasConfirm';
import { invalidateDocumentTypes } from '../../lib/schemasKeys';

const EXPIRY_RULES: readonly DocumentExpiryRule[] = [
  'EndDateRequired',
  'EndDateOptional',
  'NoExpiry',
];
const CAPTURE_FORMATS: readonly DocumentCaptureFormat[] = ['Page', 'Card'];

/** Contract §3.2: ASCII, büyük harfle başlar, 2-40 karakter. */
const CODE_PATTERN = /^[A-Z][A-Za-z0-9]{1,39}$/;
/** Contract §3.2: `Other` ayrılmıştır, asla oluşturulamaz (benzersizlik büyük/küçük harf duyarsız). */
const RESERVED_CODE = 'other';
const LABEL_MAX = 160;
const SORT_ORDER_PATTERN = /^\d{1,9}$/;
const FORM_ID = 'document-type-form';

export type DocumentTypeFormTarget =
  | { mode: 'create'; entity: DocumentEntity | null }
  | { mode: 'edit'; row: DocumentTypeAdminDto };

interface DocumentTypeFormModalProps {
  target: DocumentTypeFormTarget;
  onClose: () => void;
  /** Kayıt başarılı (form kapanmadan önce çağrılır). */
  onSaved: (saved: DocumentTypeAdminDto) => void;
  /** Düzenlenen satır sunucuda yok (404): liste tazelenince satır kalkabilir. */
  onRowStale: (uuid: string) => void;
  /** Kapanışta odağın döneceği öğe (oluşturma: kalıcı "Yeni belge türü" düğmesi). */
  restoreFocusRef?: RefObject<HTMLElement | null>;
}

type FormField =
  | 'entity'
  | 'code'
  | 'labelTr'
  | 'labelEn'
  | 'labelDe'
  | 'expiryRule'
  | 'captureFormat'
  | 'sortOrder';

const CREATE_ONLY_FIELDS: readonly FormField[] = ['entity', 'code'];
const FORM_FIELDS: readonly FormField[] = [
  'entity',
  'code',
  'labelTr',
  'labelEn',
  'labelDe',
  'expiryRule',
  'captureFormat',
  'sortOrder',
];

function isEntity(value: string): value is DocumentEntity {
  return (DOCUMENT_ENTITIES as readonly string[]).includes(value);
}
function isRule(value: string): value is DocumentExpiryRule {
  return (EXPIRY_RULES as readonly string[]).includes(value);
}
function isFormat(value: string): value is DocumentCaptureFormat {
  return (CAPTURE_FORMATS as readonly string[]).includes(value);
}
function isFormField(value: string | null): value is FormField {
  return value !== null && (FORM_FIELDS as readonly string[]).includes(value);
}

/** Sunucu Cc+Cf reddi aynası: kontrol ve biçim (bidi, sıfır genişlik) karakterleri. */
function hasInvisibleChar(value: string): boolean {
  return /[\p{Cc}\p{Cf}]/u.test(value);
}

interface ServerFieldError {
  field: FormField;
  message: string;
}

/** `VALIDATION_FAILED` alan hatalarını form alanlarına çevirir (düzenlemede entity/code alanı yok). */
function serverFieldErrors(
  error: unknown,
  isEdit: boolean,
  submittedCode: string,
  t: TFunction,
): ServerFieldError[] {
  if (!isApiError(error) || error.errorCode !== 'VALIDATION_FAILED') {
    return [];
  }
  const out: ServerFieldError[] = [];
  for (const item of error.errors) {
    if (!isFormField(item.field) || (isEdit && CREATE_ONLY_FIELDS.includes(item.field))) {
      continue;
    }
    const required = item.code === 'REQUIRED';
    let message: string;
    switch (item.field) {
      case 'entity':
        message = t('documentSchemas.form.validation.entityRequired');
        break;
      case 'code':
        message = required
          ? t('documentSchemas.form.validation.codeRequired')
          : submittedCode.toLowerCase() === RESERVED_CODE
            ? t('documentSchemas.form.validation.codeReserved')
            : t('documentSchemas.form.validation.codeInvalid');
        break;
      case 'expiryRule':
        message = t('documentSchemas.form.validation.ruleRequired');
        break;
      case 'captureFormat':
        message = t('documentSchemas.form.validation.formatRequired');
        break;
      case 'sortOrder':
        message =
          required && isEdit
            ? t('documentSchemas.form.validation.sortOrderRequired')
            : t('documentSchemas.form.validation.sortOrderInvalid');
        break;
      default:
        message = required
          ? t('documentSchemas.form.validation.labelRequired')
          : t('documentSchemas.form.validation.labelInvalid', { max: LABEL_MAX });
    }
    out.push({ field: item.field, message });
  }
  return out.sort((a, b) => FORM_FIELDS.indexOf(a.field) - FORM_FIELDS.indexOf(b.field));
}

/** İstemci doğrulaması sunucuyu (§4.1.3) UX için aynalar; karar sunucudadır. */
function buildSchema(t: TFunction, isEdit: boolean) {
  const label = z.string().superRefine((value, ctx) => {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      ctx.addIssue({ code: 'custom', message: t('documentSchemas.form.validation.labelRequired') });
    } else if (trimmed.length > LABEL_MAX || hasInvisibleChar(trimmed)) {
      ctx.addIssue({
        code: 'custom',
        message: t('documentSchemas.form.validation.labelInvalid', { max: LABEL_MAX }),
      });
    }
  });
  return z.object({
    // Düzenlemede entity/code gönderilmez ve girdisi yoktur → istemci kuralı uygulanmaz.
    entity: isEdit
      ? z.string()
      : z
          .string()
          .refine(
            (value): boolean => isEntity(value),
            t('documentSchemas.form.validation.entityRequired'),
          ),
    code: isEdit
      ? z.string()
      : z.string().superRefine((value, ctx) => {
          if (value.length === 0) {
            ctx.addIssue({
              code: 'custom',
              message: t('documentSchemas.form.validation.codeRequired'),
            });
          } else if (value.toLowerCase() === RESERVED_CODE) {
            ctx.addIssue({
              code: 'custom',
              message: t('documentSchemas.form.validation.codeReserved'),
            });
          } else if (!CODE_PATTERN.test(value)) {
            ctx.addIssue({
              code: 'custom',
              message: t('documentSchemas.form.validation.codeInvalid'),
            });
          }
        }),
    labelTr: label,
    labelEn: label,
    labelDe: label,
    // `: boolean` bilerek: tip koruyucu (ya da TS'in çıkardığı yüklem) çıktı tipini girdiden ayırır
    // ve `zodResolver`'ın tipi uymaz. Daraltma `toSubmission`da yapılır.
    expiryRule: z
      .string()
      .refine((value): boolean => isRule(value), t('documentSchemas.form.validation.ruleRequired')),
    captureFormat: z
      .string()
      .refine(
        (value): boolean => isFormat(value),
        t('documentSchemas.form.validation.formatRequired'),
      ),
    // Oluşturmada boş = sunucu sona ekler; düzenlemede `PUT` tam değişimdir → zorunlu.
    sortOrder: z.string().superRefine((value, ctx) => {
      const trimmed = value.trim();
      if (trimmed.length === 0) {
        if (isEdit) {
          ctx.addIssue({
            code: 'custom',
            message: t('documentSchemas.form.validation.sortOrderRequired'),
          });
        }
        return;
      }
      if (!SORT_ORDER_PATTERN.test(trimmed)) {
        ctx.addIssue({
          code: 'custom',
          message: t('documentSchemas.form.validation.sortOrderInvalid'),
        });
      }
    }),
    isActive: z.boolean(),
  });
}

/** Hepsi dize (yerel select/input) + `isActive`. */
type FormValues = z.infer<ReturnType<typeof buildSchema>>;

/** Doğrulanmış form → tel gövdesi (contract §4.1.3 / §4.1.4). */
type Submission =
  | { mode: 'create'; body: CreateDocumentTypeRequest }
  | { mode: 'edit'; uuid: string; body: UpdateDocumentTypeRequest };

/** Şema geçtikten sonra tipleri daraltır; ulaşılamaz bir uyumsuzlukta `null` (gönderilmez). */
function toSubmission(values: FormValues, row: DocumentTypeAdminDto | null): Submission | null {
  if (!isRule(values.expiryRule) || !isFormat(values.captureFormat)) {
    return null;
  }
  const sortOrder = values.sortOrder.trim();
  const shared = {
    labelTr: values.labelTr.trim(),
    labelEn: values.labelEn.trim(),
    labelDe: values.labelDe.trim(),
    expiryRule: values.expiryRule,
    captureFormat: values.captureFormat,
  };
  if (row) {
    if (sortOrder.length === 0) {
      return null;
    }
    return {
      mode: 'edit',
      uuid: row.uuid,
      body: { ...shared, sortOrder: Number(sortOrder), isActive: values.isActive },
    };
  }
  if (!isEntity(values.entity)) {
    return null;
  }
  return {
    mode: 'create',
    body: {
      entity: values.entity,
      code: values.code,
      ...shared,
      // Boş = sunucu bu belge sahibinin sonuna ekler; `isActive` gönderilmez (varsayılan true).
      ...(sortOrder.length > 0 ? { sortOrder: Number(sortOrder) } : {}),
    },
  };
}

export function DocumentTypeFormModal({
  target,
  onClose,
  onSaved,
  onRowStale,
  restoreFocusRef,
}: DocumentTypeFormModalProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const row = target.mode === 'edit' ? target.row : null;
  const isEdit = row !== null;
  const inflightRef = useRef(false);
  const submitRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [refocusAfterError, setRefocusAfterError] = useState(false);
  const [confirm, setConfirm] = useState<{
    submission: Extract<Submission, { mode: 'edit' }>;
    diff: ConfirmDiff;
  } | null>(null);

  const schema = useMemo(() => buildSchema(t, isEdit), [t, isEdit]);

  const { register, handleSubmit, setError, control, watch, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      entity: row?.entity ?? (target.mode === 'create' ? (target.entity ?? '') : ''),
      code: row?.code ?? '',
      labelTr: row?.labelTr ?? '',
      labelEn: row?.labelEn ?? '',
      labelDe: row?.labelDe ?? '',
      expiryRule: row?.expiryRule ?? '',
      captureFormat: row?.captureFormat ?? '',
      sortOrder: row ? String(row.sortOrder) : '',
      isActive: row?.isActive ?? true,
    },
  });

  const mutation = useMutation({
    mutationFn: (submission: Submission) =>
      submission.mode === 'edit'
        ? documentSchemasApi.updateDocumentType(submission.uuid, submission.body)
        : documentSchemasApi.createDocumentType(submission.body),
    onSuccess: (saved) => {
      invalidateDocumentTypes(queryClient);
      onSaved(saved);
      onClose();
    },
    onError: (error, submission) => {
      if (hasErrorCode(error, 'DOCUMENT_TYPE_NOT_FOUND')) {
        // Tür artık yok: liste tazelenir, form uyarıyı gösterir, kaydet kilitlenir.
        invalidateDocumentTypes(queryClient);
        if (row) {
          onRowStale(row.uuid);
        }
        setRefocusAfterError(true);
        return;
      }
      if (!isEdit && hasErrorCode(error, 'DOCUMENT_TYPE_CODE_EXISTS')) {
        setError(
          'code',
          { type: 'server', message: resolveApiErrorMessage(error, t) },
          { shouldFocus: true },
        );
        return;
      }
      const submittedCode = submission.mode === 'create' ? submission.body.code : '';
      const mapped = serverFieldErrors(error, isEdit, submittedCode, t);
      mapped.forEach((item, index) => {
        setError(
          item.field,
          { type: 'server', message: item.message },
          { shouldFocus: index === 0 },
        );
      });
      if (mapped.length === 0) {
        setRefocusAfterError(true);
      }
    },
    onSettled: () => {
      inflightRef.current = false;
    },
  });

  // Gönderimde "Kaydet" kilitlenir; odak (tıklamada ya da onay kapanışında) <body>'ye düşer. Alana
  // bağlanamayan bir hatadan sonra, odak hâlâ kayıpsa düğmeye döner (404'te kaydet kilitli → Vazgeç).
  useEffect(() => {
    if (!refocusAfterError || mutation.isPending) {
      return;
    }
    setRefocusAfterError(false);
    if (document.activeElement === null || document.activeElement === document.body) {
      const target = submitRef.current?.disabled ? cancelRef.current : submitRef.current;
      target?.focus();
    }
  }, [refocusAfterError, mutation.isPending]);

  function submit(submission: Submission) {
    // Eşzamanlı çift Enter: RHF doğrulaması async → senkron ref ile ikinci gönderimi kes.
    if (inflightRef.current || mutation.isPending) {
      return;
    }
    inflightRef.current = true;
    mutation.mutate(submission);
  }

  const onSubmit = handleSubmit((values) => {
    if (inflightRef.current || mutation.isPending) {
      return;
    }
    const submission = toSubmission(values, row);
    if (submission === null) {
      return;
    }
    if (submission.mode === 'edit' && row) {
      const diff = diffNeedsConfirm(row, submission.body);
      if (needsConfirm(diff)) {
        setConfirm({ submission, diff });
        return;
      }
    }
    submit(submission);
  });

  const notFound = hasErrorCode(mutation.error, 'DOCUMENT_TYPE_NOT_FOUND');
  const codeExistsHandled = !isEdit && hasErrorCode(mutation.error, 'DOCUMENT_TYPE_CODE_EXISTS');
  const mappedCount = serverFieldErrors(mutation.error, isEdit, '', t).length;
  const totalFieldErrors = isApiError(mutation.error) ? mutation.error.errors.length : 0;
  const bannerError =
    mutation.isError && !codeExistsHandled && (mappedCount === 0 || totalFieldErrors > mappedCount);

  const entityOptions: SelectOption[] = DOCUMENT_ENTITIES.map((value) => ({
    value,
    label: t(`documentSchemas.entity.${value}`),
  }));
  const ruleOptions: SelectOption[] = EXPIRY_RULES.map((value) => ({
    value,
    label: t(`documentSchemas.expiryRule.${value}`),
  }));
  const formatOptions: SelectOption[] = CAPTURE_FORMATS.map((value) => ({
    value,
    label: t(`documentSchemas.captureFormat.option.${value}`),
  }));

  const selectedRule = watch('expiryRule');
  const ruleHint = isRule(selectedRule) ? t(`documents.expiryHint.${selectedRule}`) : null;

  const errors = formState.errors;
  const err = {
    entity: errors.entity?.message,
    code: errors.code?.message,
    labelTr: errors.labelTr?.message,
    labelEn: errors.labelEn?.message,
    labelDe: errors.labelDe?.message,
    expiryRule: errors.expiryRule?.message,
    captureFormat: errors.captureFormat?.message,
    sortOrder: errors.sortOrder?.message,
  };

  const pending = mutation.isPending;

  return (
    <>
      <Modal
        open
        onOpenChange={(next) => {
          if (!next && !pending) {
            onClose();
          }
        }}
        title={
          isEdit ? t('documentSchemas.form.edit.title') : t('documentSchemas.form.create.title')
        }
        size="lg"
        closeLabel={t('common.close')}
        restoreFocusRef={restoreFocusRef}
        footer={
          <>
            <Button ref={cancelRef} variant="secondary" onClick={onClose} disabled={pending}>
              {t('common.cancel')}
            </Button>
            <Button
              ref={submitRef}
              type="submit"
              form={FORM_ID}
              loading={pending}
              disabled={notFound}
            >
              {isEdit ? t('documentSchemas.form.save') : t('documentSchemas.form.create')}
            </Button>
          </>
        }
      >
        <form id={FORM_ID} className="flex flex-col gap-5" noValidate onSubmit={onSubmit}>
          {bannerError ? (
            <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
          ) : null}

          {row ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1">
                <span className="text-xs text-muted">{t('documentSchemas.form.field.entity')}</span>
                <span className="text-sm font-medium text-fg">
                  {t(`documentSchemas.entity.${row.entity}`)}
                </span>
              </div>
              <div className="flex flex-col items-start gap-1">
                <span className="text-xs text-muted">{t('documentSchemas.form.field.code')}</span>
                <code className="max-w-full break-all rounded bg-surface-muted px-1.5 py-0.5 text-xs text-fg">
                  {row.code}
                </code>
              </div>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                htmlFor="document-type-entity"
                label={t('documentSchemas.form.field.entity')}
                error={err.entity}
                required
              >
                <Select
                  id="document-type-entity"
                  options={entityOptions}
                  placeholder={t('documentSchemas.form.selectPlaceholder')}
                  invalid={Boolean(err.entity)}
                  aria-describedby={fieldDescribedBy('document-type-entity', err.entity, false)}
                  {...register('entity')}
                />
              </Field>
              <Field
                htmlFor="document-type-code"
                label={t('documentSchemas.form.field.code')}
                error={err.code}
                hint={t('documentSchemas.form.codeHint')}
                required
              >
                <Input
                  id="document-type-code"
                  autoComplete="off"
                  spellCheck={false}
                  maxLength={40}
                  invalid={Boolean(err.code)}
                  aria-describedby={fieldDescribedBy('document-type-code', err.code, true)}
                  {...register('code')}
                />
              </Field>
            </div>
          )}

          <FormSection legend={t('documentSchemas.form.section.labels')}>
            <Field
              htmlFor="document-type-label-tr"
              label={t('documentSchemas.form.field.labelTr')}
              error={err.labelTr}
              required
            >
              <Input
                id="document-type-label-tr"
                lang="tr"
                maxLength={LABEL_MAX}
                invalid={Boolean(err.labelTr)}
                aria-describedby={fieldDescribedBy('document-type-label-tr', err.labelTr, false)}
                {...register('labelTr')}
              />
            </Field>
            <Field
              htmlFor="document-type-label-en"
              label={t('documentSchemas.form.field.labelEn')}
              error={err.labelEn}
              required
            >
              <Input
                id="document-type-label-en"
                lang="en"
                maxLength={LABEL_MAX}
                invalid={Boolean(err.labelEn)}
                aria-describedby={fieldDescribedBy('document-type-label-en', err.labelEn, false)}
                {...register('labelEn')}
              />
            </Field>
            <Field
              htmlFor="document-type-label-de"
              label={t('documentSchemas.form.field.labelDe')}
              error={err.labelDe}
              required
            >
              <Input
                id="document-type-label-de"
                lang="de"
                maxLength={LABEL_MAX}
                invalid={Boolean(err.labelDe)}
                aria-describedby={fieldDescribedBy('document-type-label-de', err.labelDe, false)}
                {...register('labelDe')}
              />
            </Field>
          </FormSection>

          <FormSection legend={t('documentSchemas.form.section.rules')}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                htmlFor="document-type-rule"
                label={t('documentSchemas.form.field.expiryRule')}
                error={err.expiryRule}
                hint={ruleHint}
                required
              >
                <Select
                  id="document-type-rule"
                  options={ruleOptions}
                  placeholder={t('documentSchemas.form.selectPlaceholder')}
                  invalid={Boolean(err.expiryRule)}
                  aria-describedby={fieldDescribedBy(
                    'document-type-rule',
                    err.expiryRule,
                    ruleHint !== null,
                  )}
                  {...register('expiryRule')}
                />
              </Field>
              <Field
                htmlFor="document-type-format"
                label={t('documentSchemas.form.field.captureFormat')}
                error={err.captureFormat}
                hint={t('documentSchemas.form.captureFormatHint')}
                required
              >
                <Select
                  id="document-type-format"
                  options={formatOptions}
                  placeholder={t('documentSchemas.form.selectPlaceholder')}
                  invalid={Boolean(err.captureFormat)}
                  aria-describedby={fieldDescribedBy(
                    'document-type-format',
                    err.captureFormat,
                    true,
                  )}
                  {...register('captureFormat')}
                />
              </Field>
            </div>
            <Field
              htmlFor="document-type-sort"
              label={t('documentSchemas.form.field.sortOrder')}
              error={err.sortOrder}
              hint={isEdit ? undefined : t('documentSchemas.form.sortOrderHint')}
              required={isEdit}
            >
              <Input
                id="document-type-sort"
                type="number"
                inputMode="numeric"
                min={0}
                step={1}
                className="w-32 tnum"
                invalid={Boolean(err.sortOrder)}
                aria-describedby={fieldDescribedBy('document-type-sort', err.sortOrder, !isEdit)}
                {...register('sortOrder')}
              />
            </Field>
          </FormSection>

          {isEdit ? (
            <Controller
              control={control}
              name="isActive"
              render={({ field }) => (
                <Switch
                  ref={field.ref}
                  name={field.name}
                  checked={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  label={t('documentSchemas.form.field.isActive')}
                  description={t('documentSchemas.form.isActiveHint')}
                />
              )}
            />
          ) : null}
        </form>
      </Modal>

      {row && confirm ? (
        <ConfirmDialog
          open
          onOpenChange={(next) => {
            if (!next) {
              setConfirm(null);
            }
          }}
          title={confirmTitle(confirm.diff, t)}
          confirmLabel={confirmButton(confirm.diff, t)}
          body={
            <ConfirmBody
              diff={confirm.diff}
              // Gönderilecek etiket (yeni ad), arayüz dilinde.
              label={documentTypeLabel(
                [{ ...row, ...confirm.submission.body }],
                row.entity,
                row.code,
                i18n.language,
              )}
              entity={t(`documentSchemas.entity.${row.entity}`)}
              from={t(`documentSchemas.expiryRule.${row.expiryRule}`)}
              to={t(`documentSchemas.expiryRule.${confirm.submission.body.expiryRule}`)}
            />
          }
          // Saf kapı: onay kapanır, gönderim formun kendi mutasyonundan geçer → hata formda görünür.
          onConfirm={() => {
            const submission = confirm.submission;
            setConfirm(null);
            submit(submission);
          }}
        />
      ) : null}
    </>
  );
}

function confirmTitle(diff: ConfirmDiff, t: TFunction): string {
  if (diff.deactivate && diff.expiryChanged) {
    return t('documentSchemas.confirm.both.title');
  }
  return diff.deactivate
    ? t('documentSchemas.confirm.deactivate.title')
    : t('documentSchemas.confirm.rule.title');
}

function confirmButton(diff: ConfirmDiff, t: TFunction): string {
  if (diff.deactivate && diff.expiryChanged) {
    return t('documentSchemas.confirm.both.confirm');
  }
  return diff.deactivate
    ? t('documentSchemas.confirm.deactivate.confirm')
    : t('documentSchemas.confirm.rule.confirm');
}

function ConfirmBody({
  diff,
  label,
  entity,
  from,
  to,
}: {
  diff: ConfirmDiff;
  label: string;
  entity: string;
  from: string;
  to: string;
}) {
  const { t } = useTranslation();
  const lead =
    diff.deactivate && diff.expiryChanged
      ? t('documentSchemas.confirm.both.lead', { label, entity, from, to })
      : diff.deactivate
        ? t('documentSchemas.confirm.deactivate.lead', { label, entity })
        : t('documentSchemas.confirm.rule.lead', { label, entity, from, to });
  return (
    <div className="flex flex-col gap-2">
      <p className="break-words">{lead}</p>
      <ul className="flex list-disc flex-col gap-1 pl-5">
        {diff.deactivate ? (
          <>
            <li>{t('documentSchemas.confirm.deactivate.permits')}</li>
            <li>{t('documentSchemas.confirm.deactivate.documents')}</li>
          </>
        ) : null}
        {diff.expiryChanged ? (
          <>
            <li>{t('documentSchemas.confirm.rule.retroactive')}</li>
            {diff.toRequired ? <li>{t('documentSchemas.confirm.rule.toRequired')}</li> : null}
          </>
        ) : null}
      </ul>
    </div>
  );
}
